import { useCallback } from 'react';
import { ClipObject } from '../../../../context/clips';
import {
  ClipDeletionFragment,
  ClipDeletionFragmentDoc,
} from '../../operation.generated';
import useOptimisticDeleteClips from '../useOptimisticDeleteClips';
import useRestoreClips from '../useRestoreClips';
import { useStore } from './common';
import useClipCacheUtilities from './useClipCacheUtilities';
import { toast } from '../../../Toaster';
import { getApolloMutationErrorMessage } from '../../../../util/apolloError';

const useDeleteClips = () => {
  const [optimisticDeleteMutation] = useOptimisticDeleteClips();
  const [restoreMutation] = useRestoreClips();
  const selectedClipIds = useStore(state => state.selectedClipIds);
  const setSelectedClipIds = useStore(state => state.setSelectedClipIds);
  const { removeClipFromCache } = useClipCacheUtilities();

  const deleteClip = useCallback(
    // clipIds passed in are selected clip ids
    (clips: ClipObject[], clipIds: string[]) => {
      clips.forEach(clip => {
        if (clip.streamRequest) {
          clip.streamRequest.abort();
        }
      });

      /**
       * If single clip and is failure, we don't need to worry about handling
       * a list of clips because a failed clip does not allow for batch actions
       */
      if (clips.length === 1 && clips[0].failedWithError) {
        removeClipFromCache(clips[0], clips[0].projectId);
        return;
      }

      const deletionPromise = new Promise<void>((resolve, reject) => {
        let updateCount = 0;
        optimisticDeleteMutation({
          variables: { inputs: { clipIds } },
          // for optimistic responses `update` is called twice, for the optimistic response
          // and when the server actually responds
          // https://www.apollographql.com/docs/react/data/mutations/#making-all-other-cache-updates
          update: (proxy, { data }) => {
            updateCount += 1;
            const mutationCompleted = updateCount === 2;
            const isDeleted = data?.deleteClips.success || false;
            // clear all selected ids
            setSelectedClipIds(selectedClipIds.clear());

            // Update Apollo cache state (if entity exists) for each selected clip
            clipIds.forEach(clipId => {
              const cachedClip = proxy.readFragment<ClipDeletionFragment>({
                id: proxy.identify({
                  id: clipId,
                  __typename: 'Clip',
                }),
                fragment: ClipDeletionFragmentDoc,
              });
              if (cachedClip) {
                proxy.writeFragment<ClipDeletionFragment>({
                  id: proxy.identify({
                    id: cachedClip?.id,
                    __typename: 'Clip',
                  }),
                  fragment: ClipDeletionFragmentDoc,
                  data: {
                    ...cachedClip,
                    isDeleted,
                  },
                });
              }
            });

            // Handle promise
            if (data?.deleteClips.success === false) {
              toast({
                message: data.deleteClips.message,
                type: 'error',
              });
              reject(data.deleteClips.message);
            } else if (mutationCompleted) {
              resolve();
            }
          },
        }).catch(error => {
          const message = getApolloMutationErrorMessage(
            error,
            'Failed to delete clips'
          );
          toast({
            message,
            type: 'error',
          });
          reject(message);
        });
      });

      toast({
        message: `Deleted ${clipIds.length} clip${
          clipIds.length > 1 ? 's' : ''
        }.`,
        cta: {
          label: 'undo',
          onClick: () => {
            deletionPromise
              .then(() => {
                restoreMutation({
                  variables: { inputs: { clipIds } },
                  update: (proxy, { data }) => {
                    if (!data?.restoreClips.success) {
                      toast({
                        message: 'Error restoring clip',
                        type: 'error',
                      });
                    }
                  },
                });
              })
              .catch(() => {
                // handled inside of optimistic delete
              });
          },
        },
      });
    },
    [
      optimisticDeleteMutation,
      restoreMutation,
      selectedClipIds,
      setSelectedClipIds,
    ]
  );
  return deleteClip;
};

export default useDeleteClips;
